import useSEO from '@/hooks/useSEO';
import { commissionFAQs } from './data';
import CommissionModelsSection from './components/CommissionModelsSection';
import CommissionCalculationSection from './components/CommissionCalculationSection';
import CommissionImpactSection from './components/CommissionImpactSection';
import CommissionComparisonSection from './components/CommissionComparisonSection';
import HowToReduceCommissionSection from './components/HowToReduceCommissionSection';
import CommissionFAQSection from './components/CommissionFAQSection';
import RelatedGuidesSection from './components/RelatedGuidesSection';

export default function ForexCommission() {
  useSEO({
    /* @replace:commission-title */

    title: 'Forex Commission Explained: Rates, Models and Calculation',
    /* @replace:commission-description */

    description: 'Learn how forex commission is charged per lot and per side, compare common pricing models, and calculate the full round-turn commission on a trade.',
    /* @replace:commission-keywords */

    keywords: 'forex commission, commission per lot, round turn commission, forex broker fees, commission calculation',
    /* @replace:commission-canonical */

    canonical: '/forex-commission/',
  });

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: commissionFAQs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <div className="min-h-screen">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />

      {/* Hero */}
      <section className="relative w-full overflow-hidden bg-foreground-950">
        {/* Decorative grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: 'radial-gradient(circle, oklch(var(--accent-500)) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />

        {/* Decorative: large geometric ring top-right */}
        <div className="absolute -top-[200px] -right-[200px] w-[600px] h-[600px] rounded-full border border-accent-500/10" />
        <div className="absolute -top-[140px] -right-[140px] w-[480px] h-[480px] rounded-full border border-accent-500/8" />
        <div className="absolute -top-[80px] -right-[80px] w-[360px] h-[360px] rounded-full border border-accent-500/6" />

        {/* Decorative: diagonal accent line top-left */}
        <div className="absolute top-[120px] -left-8 w-[200px] h-[1px] bg-gradient-to-r from-accent-500/0 via-accent-500/20 to-accent-500/0 rotate-[30deg]" />
        <div className="absolute top-[200px] -left-12 w-[160px] h-[1px] bg-gradient-to-r from-accent-500/0 via-accent-500/15 to-accent-500/0 rotate-[30deg]" />

        {/* Decorative: subtle dot cluster bottom-left */}
        <div className="absolute bottom-[80px] left-[8%] flex gap-2">
          <span className="w-1 h-1 rounded-full bg-accent-500/20" />
          <span className="w-1.5 h-1.5 rounded-full bg-accent-500/30" />
          <span className="w-1 h-1 rounded-full bg-accent-500/20" />
          <span className="w-2 h-2 rounded-full bg-accent-500/25" />
          <span className="w-1 h-1 rounded-full bg-accent-500/15" />
        </div>

        {/* Decorative: thin vertical bars right side */}
        <div className="absolute top-1/3 right-[5%] flex gap-3">
          <div className="w-[1px] h-16 bg-accent-500/10" />
          <div className="w-[1px] h-24 bg-accent-500/15" />
          <div className="w-[1px] h-12 bg-accent-500/8" />
          <div className="w-[1px] h-20 bg-accent-500/12" />
        </div>

        {/* Decorative: large soft glow behind content */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] rounded-full opacity-[0.08] blur-[120px] bg-accent-500" />

        {/* Decorative: tiny crosses scattered */}
        <span className="absolute top-[25%] right-[20%] text-accent-500/15 text-[20px] font-light select-none">+</span>
        <span className="absolute bottom-[30%] left-[15%] text-accent-500/12 text-[16px] font-light select-none">+</span>
        <span className="absolute top-[40%] left-[25%] text-accent-500/10 text-[14px] font-light select-none">+</span>

        {/* Main content */}
        <div className="relative max-w-main mx-auto px-4 md:px-6 pt-20 md:pt-28 pb-20 md:pb-24 text-center">
          {/* Heading */}
          <h1 className="text-[40px] md:text-[58px] font-[750] text-background-50 leading-[1.08] tracking-tight mb-5">
            What is the
            <br />
            <span className="relative inline-block">
              <span className="relative z-10">forex commission</span>
              <span className="absolute left-0 right-0 bottom-[2px] h-[5px] rounded-full bg-accent-500/70" />
            </span>
          </h1>

          <p className="text-[16px] md:text-[18px] text-foreground-400 leading-relaxed max-w-[620px] mx-auto mb-10">
            The separate fee on every trade. Visible on ECN accounts, hidden in the spread on standard ones. Either way — you pay it.
          </p>

          {/* Formula card */}
          <div className="max-w-[720px] mx-auto bg-foreground-900/80 border border-foreground-800/60 rounded-[24px] p-5 md:p-9">
            <div className="flex items-center justify-center gap-2 mb-5 md:mb-6">
              <div className="w-8 h-[1px] bg-accent-500/30" />
              <span className="text-[11px] font-semibold text-accent-400 uppercase tracking-[0.2em]">At a glance</span>
              <div className="w-8 h-[1px] bg-accent-500/30" />
            </div>

            <div className="text-[20px] md:text-[28px] font-[750] text-background-50 leading-tight mb-5 md:mb-6 tracking-tight text-center">
              One trade, two sides, one round-turn fee
            </div>

            {/* Mobile: 2-row layout; Desktop: single row */}
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">

              {/* Row 1 on mobile: Open + connector + Close */}
              <div className="flex items-center gap-2 md:contents">
                {/* Open side */}
                <div className="flex-1 bg-foreground-950 rounded-[14px] p-4 md:p-5 border border-foreground-800/50 text-center">
                  <div className="flex items-center justify-center gap-1.5 mb-1.5 md:mb-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-accent-500" />
                    <span className="text-[10px] md:text-[11px] font-bold text-accent-400 uppercase tracking-wider">Open</span>
                  </div>
                  <div className="text-[26px] md:text-[32px] font-[750] text-background-50 leading-none mb-1">$3.50</div>
                  <div className="text-[11px] md:text-[12px] text-foreground-500">per standard lot</div>
                </div>

                {/* Plus connector — visible only in mobile row 1 */}
                <div className="flex-shrink-0 flex flex-col items-center gap-0.5 text-foreground-600 md:hidden">
                  <span className="text-[16px] font-light">+</span>
                </div>

                {/* Close side */}
                <div className="flex-1 bg-foreground-950 rounded-[14px] p-4 md:p-5 border border-foreground-800/50 text-center">
                  <div className="flex items-center justify-center gap-1.5 mb-1.5 md:mb-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-accent-500" />
                    <span className="text-[10px] md:text-[11px] font-bold text-accent-400 uppercase tracking-wider">Close</span>
                  </div>
                  <div className="text-[26px] md:text-[32px] font-[750] text-background-50 leading-none mb-1">$3.50</div>
                  <div className="text-[11px] md:text-[12px] text-foreground-500">per standard lot</div>
                </div>

                {/* Desktop-only plus connector (between Open and Close) */}
                <div className="hidden md:flex flex-col items-center gap-1 text-foreground-600 order-[-1] md:order-none" style={{order: 'unset'}}>
                </div>
              </div>

              {/* Desktop-only plus connector */}
              <div className="hidden md:flex flex-col items-center gap-1 text-foreground-600">
                <span className="text-[18px] font-light">+</span>
                <span className="text-[10px] text-foreground-600">close</span>
              </div>

              {/* Row 2 on mobile: equals label + Round Turn */}
              <div className="flex items-center gap-3 md:contents">
                {/* Equals connector */}
                <div className="flex-shrink-0 flex items-center justify-center md:hidden">
                  <span className="text-[22px] font-light text-accent-400">=</span>
                </div>
                {/* Desktop equals */}
                <div className="hidden md:flex md:flex-col items-center gap-1">
                  <span className="text-[18px] font-light text-accent-400">=</span>
                </div>

                {/* Round-turn result */}
                <div className="flex-1 bg-accent-500/10 rounded-[14px] p-4 md:p-5 border border-accent-500/30 text-center">
                  <div className="flex items-center justify-center gap-1.5 mb-1.5 md:mb-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-accent-400 animate-pulse" />
                    <span className="text-[10px] md:text-[11px] font-bold text-accent-400 uppercase tracking-wider">Round Turn</span>
                  </div>
                  <div className="text-[26px] md:text-[32px] font-[750] text-accent-400 leading-none mb-1">$7.00</div>
                  <div className="text-[11px] md:text-[12px] text-accent-500/60">per complete trade</div>
                </div>
              </div>

            </div>

            {/* Bottom note */}
            <div className="mt-4 md:mt-5 pt-4 md:pt-5 border-t border-foreground-800/40">
              <div className="flex items-start justify-center gap-2">
                <i className="ri-information-line w-4 h-4 flex items-center justify-center text-foreground-600 mt-0.5 flex-shrink-0" />
                <p className="text-[12px] md:text-[13px] leading-relaxed text-foreground-500">
                  Typical ECN range: <strong className="text-foreground-400">$3.50–$7.00</strong> per side. The round-turn cost hits your account twice — once when you open, once when you close.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CommissionModelsSection />
      <CommissionCalculationSection />
      <CommissionImpactSection />
      <CommissionComparisonSection />
      <HowToReduceCommissionSection />
      <CommissionFAQSection />
      <RelatedGuidesSection />
    </div>
  );
}